// src/components/StepUpModalHost.tsx
/**
 * The one step-up prompt in the app: a single react-modal, mounted once next to the routed tree, in
 * front of whatever page happens to be open.
 *
 * WHY ONE HOST INSTEAD OF A MODAL PER PAGE. Three different things ask for a second factor and none
 * of them owns a screen: `StepUpGate` blocks an admin page it will not render, a card on the
 * security page asks politely before unlocking itself, and the axios interceptors dispatch
 * `stepUpRequired()` the moment any request comes back `403 mfa_required` — which can happen on any
 * screen, at any time, including one that has no modal of its own. Redux holds the request
 * (`mfa.promptOpen` / `mfa.promptBlocking`) and this component is the only thing that answers it, so
 * two callers can never put two prompts on screen and no caller has to invent its own recovery from
 * a 403.
 *
 * BLOCKING VERSUS ASKING. A prompt raised by a gate is blocking: the page behind it is refusing to
 * render until the session is verified, so an Escape key would leave the reader staring at a
 * placeholder with no way forward. Its footer offers the honest exit instead — back to the account
 * page. A prompt raised by an interceptor is not blocking: the screen underneath is a real screen
 * the reader may want to keep reading.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { errText, isRateLimited } from '../api/errors';
import { recoverWithCode, verifyTotp } from '../api/securityApi';
import {
    RECOVER_RATE_LIMIT_HINT,
    TOTP_CODE_LENGTH,
    VERIFY_RATE_LIMIT_HINT,
    isRecoveryCodeShape,
    isTotpCodeComplete,
    normalizeTotpCode,
} from '../security/mfaContract';
import { closeStepUp, recoveryCodeUsed, selectMfa, stepUpVerified } from '../store/mfaSlice';
import { loadMfaStatus } from '../store/mfaThunks';
import type { AppDispatch } from '../store/store';

const VERIFY_FAILED = 'Overenie zlyhalo. Skúste to znova.';

/** Declared out here rather than inline so the object identity is stable across renders — react-modal
 *  applies these to nodes it manages itself. `position` is deliberately not set: it is one of the few
 *  CSS properties csstype types without a plain-string fallback, and this object is not contextually
 *  typed. #root is `<body>` in public/index.html, hence `ariaHideApp={false}` below. */
const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '16px',
        zIndex: 1000,
    },
    content: {
        inset: 'auto',
        width: '100%',
        maxWidth: '440px',
        margin: 'auto',
        padding: '0',
        border: 'none',
        borderRadius: '10px',
        background: 'none',
        overflow: 'visible',
    },
};

const StepUpModalHost: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { promptOpen, promptBlocking, loadState, enabled } = useSelector(selectMfa);

    const [mode, setMode] = useState<'code' | 'recovery'>('code');
    const [code, setCode] = useState('');
    const [recovery, setRecovery] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement | null>(null);
    /** Whether this opening of the prompt has already asked the server for a status. */
    const statusRequestedRef = useRef(false);

    /** A prompt always opens on a clean form: a code left over from a previous attempt has expired,
     *  and a stale error would read as a verdict on something the reader has not typed yet. */
    useEffect(() => {
        if (promptOpen) {
            setMode('code');
            setCode('');
            setRecovery('');
            setError(null);
            setBusy(false);
        }
    }, [promptOpen]);

    /**
     * Make sure there is a real status behind the prompt — the interceptor can raise it on a screen
     * that never loaded one.
     *
     * Once per opening, and never on a loop: `loadMfaStatus` deliberately does NOT skip a failed
     * load (so a retry button needs no flag), so an effect that simply re-ran while
     * `loadState !== 'ready'` would retry a failing request forever.
     */
    useEffect(() => {
        if (!promptOpen) {
            statusRequestedRef.current = false;
            return;
        }
        if (statusRequestedRef.current || loadState === 'ready') {
            return;
        }
        statusRequestedRef.current = true;
        void dispatch(loadMfaStatus());
    }, [promptOpen, loadState, dispatch]);

    /** `?.` on purpose: the loading and not-enrolled variants have no field to focus. */
    const focusInput = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    // Also on `mode` and `loadState`: the field appears when the status resolves and is replaced
    // when the reader switches to a recovery code, and in both cases it should be ready to type in.
    useEffect(() => {
        if (promptOpen) {
            focusInput();
        }
    }, [promptOpen, mode, loadState, focusInput]);

    const submit = async (kind: 'code' | 'recovery', value: string) => {
        if (busy) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            if (kind === 'recovery') {
                await recoverWithCode(value);
                dispatch(stepUpVerified());
                dispatch(recoveryCodeUsed());
            } else {
                await verifyTotp(value);
                dispatch(stepUpVerified());
            }
            // The server is the authority on `enabled` and on how many recovery codes are left, and
            // `stepUpVerified` deliberately invents neither. Forced, because the status this session
            // already has is `ready` and would otherwise be reused.
            void dispatch(loadMfaStatus({ force: true }));
        } catch (err) {
            // Nothing is logged here: an axios error carries the request headers, and those carry
            // the bearer token.
            const message = errText(err, VERIFY_FAILED);
            const hint = kind === 'recovery' ? RECOVER_RATE_LIMIT_HINT : VERIFY_RATE_LIMIT_HINT;
            // "Priveľa pokusov" on its own leaves an admin locked out of their own site with no idea
            // whether to wait a minute or an hour, and the two windows differ.
            setError(isRateLimited(err) ? `${message} ${hint}` : message);
            if (kind === 'recovery') {
                setRecovery('');
            } else {
                setCode('');
            }
            focusInput();
        } finally {
            setBusy(false);
        }
    };

    /** Auto-submit at six digits. There is nothing else on this form to fill in, so asking for a
     *  click as well only adds a way to sit on a complete code until it expires. */
    const onCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = normalizeTotpCode(event.target.value);
        setCode(next);
        if (!busy && isTotpCodeComplete(next)) {
            void submit('code', next);
        }
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'recovery') {
            if (isRecoveryCodeShape(recovery)) {
                void submit('recovery', recovery);
            }
            return;
        }
        if (isTotpCodeComplete(code)) {
            void submit('code', code);
        }
    };

    const leaveFor = (path: string) => {
        dispatch(closeStepUp());
        navigate(path);
    };

    // `error` is not one of these: a status that could not be read still leaves a code worth trying,
    // and the reader is looking at the form for a reason. Only a status that is known and says "this
    // account has nothing enrolled" replaces the form.
    const showLoading = loadState === 'idle' || loadState === 'loading';
    const notEnrolled = loadState === 'ready' && !enabled;

    return (
        <Modal
            isOpen={promptOpen}
            onRequestClose={() => dispatch(closeStepUp())}
            onAfterOpen={focusInput}
            shouldCloseOnEsc={!promptBlocking}
            shouldCloseOnOverlayClick={!promptBlocking}
            style={modalStyles}
            ariaHideApp={false}
        >
            <Card>
                <Title>Overenie správcu</Title>

                {showLoading && <Hint>Overujem reláciu…</Hint>}

                {!showLoading && notEnrolled && (
                    <>
                        <Text>
                            Server vyžaduje dvojstupňové overenie, ale váš účet ho ešte nemá zapnuté.
                        </Text>
                        <Row>
                            <Primary type="button" onClick={() => leaveFor('/bezpecnost')}>
                                Nastaviť dvojstupňové overenie
                            </Primary>
                        </Row>
                    </>
                )}

                {!showLoading && !notEnrolled && (
                    <form onSubmit={onSubmit}>
                        {mode === 'code' ? (
                            <>
                                <Field>
                                    <Label htmlFor="stepup-totp">Kód z aplikácie</Label>
                                    <Input
                                        id="stepup-totp"
                                        ref={inputRef}
                                        name="totp"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="one-time-code"
                                        maxLength={TOTP_CODE_LENGTH}
                                        value={code}
                                        onChange={onCodeChange}
                                        /* readOnly, not disabled: a disabled field loses focus, and
                                           the whole point of clearing it after a wrong code is that
                                           the next one can be typed straight away. */
                                        readOnly={busy}
                                    />
                                </Field>
                                {error && <Banner>{error}</Banner>}
                                <Row>
                                    <Primary type="submit" disabled={busy || !isTotpCodeComplete(code)}>
                                        {busy ? 'Overujem…' : 'Overiť'}
                                    </Primary>
                                </Row>
                                <LinkButton type="button" onClick={() => setMode('recovery')}>
                                    Nemám prístup k aplikácii — použiť záložný kód
                                </LinkButton>
                            </>
                        ) : (
                            <>
                                <Field>
                                    <Label htmlFor="stepup-recovery">Záložný kód</Label>
                                    <Input
                                        id="stepup-recovery"
                                        ref={inputRef}
                                        name="recovery"
                                        type="text"
                                        autoComplete="off"
                                        autoCapitalize="characters"
                                        spellCheck={false}
                                        placeholder="XXXX-XXXX"
                                        value={recovery}
                                        onChange={(event) => setRecovery(event.target.value)}
                                        readOnly={busy}
                                    />
                                    <Hint>
                                        Kód má tvar XXXX-XXXX (8 znakov, bez 0, O, 1, I, L). Každý
                                        záložný kód platí len raz.
                                    </Hint>
                                </Field>
                                {error && <Banner>{error}</Banner>}
                                <Row>
                                    {/* Disabled until the shape is right: this endpoint is BCrypt-slow
                                        and allows five attempts per fifteen minutes, so spending one
                                        on a typo is a real cost. */}
                                    <Primary
                                        type="submit"
                                        disabled={busy || !isRecoveryCodeShape(recovery)}
                                    >
                                        {busy ? 'Overujem…' : 'Použiť záložný kód'}
                                    </Primary>
                                </Row>
                                <LinkButton type="button" onClick={() => setMode('code')}>
                                    Späť na kód z aplikácie
                                </LinkButton>
                            </>
                        )}
                    </form>
                )}

                <Footer>
                    {promptBlocking ? (
                        <>
                            <Hint>Táto stránka je dostupná až po overení.</Hint>
                            <Secondary type="button" onClick={() => leaveFor('/account')}>
                                Späť na účet
                            </Secondary>
                        </>
                    ) : (
                        <Secondary type="button" onClick={() => dispatch(closeStepUp())}>
                            Zavrieť
                        </Secondary>
                    )}
                </Footer>
            </Card>
        </Modal>
    );
};

/* ---------------- styles (folder-local copies of the Campaigns vocabulary) ---------------- */

const Card = styled.div`
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
    padding: 20px 22px;
`;

const Title = styled.h2`
    font-size: 18px;
    font-weight: 700;
    color: #141210;
    margin: 0 0 14px;
`;

const Text = styled.p`
    font-size: 14px;
    line-height: 1.6;
    color: #141210;
    margin: 0 0 14px;
`;

const Field = styled.div`
    margin-bottom: 14px;
`;

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #141210;
    margin-bottom: 6px;
`;

const Input = styled.input`
    width: 100%;
    font: inherit;
    font-size: 16px;
    letter-spacing: 0.12em;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 5px;

    &:focus {
        outline: none;
        border-color: #f56607;
        box-shadow: 0 0 0 3px rgba(245, 102, 7, 0.13);
    }
`;

const Hint = styled.p`
    font-size: 12px;
    line-height: 1.6;
    color: #777;
    margin: 8px 0 0;
`;

const Row = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
`;

const Secondary = styled.button`
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 11px 20px;
    border-radius: 5px;
    border: 1px solid #ddd;
    background: #fff;
    color: #141210;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const Primary = styled(Secondary)`
    background: #f56607;
    border-color: #f56607;
    color: #fff;
`;

const LinkButton = styled.button`
    background: none;
    border: none;
    color: #f56607;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    padding: 12px 0 0;
    text-align: left;
`;

const Banner = styled.p<{ $ok?: boolean; $warn?: boolean }>`
    background: ${(p) => (p.$warn ? '#fff4e5' : p.$ok ? '#eef7ee' : '#fdecec')};
    color: ${(p) => (p.$warn ? '#8a5300' : p.$ok ? '#256029' : '#a11')};
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 13.5px;
    line-height: 1.6;
    margin: 0 0 14px;
`;

const Footer = styled.div`
    border-top: 1px solid #eee;
    margin-top: 16px;
    padding-top: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    ${Hint} {
        margin: 0;
    }
`;

export default StepUpModalHost;
